import React, { useMemo, useRef, useState } from 'react';
import { Input, Select, Typography } from 'antd';
import { Check, X, ChevronDown, Pencil, GripVertical, Search, ChevronUp, TriangleAlert } from 'lucide-react';
import { COMPETENCIES, COMPETENCY_BY_ID, QuestionType, type CompetencySlug } from '@evaltick/shared';
import { BuilderTable, type BuilderTableColumn } from '@/components/BuilderTable';
import { BuilderChip } from '@/components/BuilderChip';
import { BuilderIconButton } from '@/components/BuilderIconButton';
import type { QuestionSetPackageRef, QuestionWithQuestionSets } from '@/services/api';
import { competencyChipColor, typeChipColor } from '@/utils/builderChipColors';
import { formatDate } from '@/utils/formatters';
import { QUESTION_TYPE_LABELS } from '@/utils/questionTypeLabels';
import styles from './QuestionSetItemsField.module.scss';

const { Text } = Typography;

const ALL = 'all' as const;

const COMPETENCY_FILTER_OPTIONS = [
  { label: 'All competencies', value: ALL },
  ...COMPETENCIES.map((c) => ({ label: c.name, value: c.id })),
];

const TYPE_FILTER_OPTIONS = [
  { label: 'All types', value: ALL },
  ...Object.entries(QUESTION_TYPE_LABELS).map(([value, label]) => ({ label, value })),
];

const competencyName = (competencyId: string) => COMPETENCY_BY_ID[competencyId]?.name ?? competencyId;

const GRID_TEMPLATE_COLUMNS = '44px 2.8fr 1.6fr 0.9fr 0.8fr 0.85fr';

const PICKER_PER_PAGE = 10;

interface QuestionSetItemsFieldProps {
  questions: QuestionWithQuestionSets[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onEditQuestion?: (question: QuestionWithQuestionSets) => void;
  /** Assessment packages that pin this question set — shown as a non-blocking warning when editing. */
  referencingPackages?: QuestionSetPackageRef[];
}

/**
 * Picker + selected-cards editor for building a QuestionSet's ordered
 * question list. Reordering uses native HTML5 drag-and-drop (no external
 * dnd library needed for a single-list reorder).
 */
export const QuestionSetItemsField: React.FC<QuestionSetItemsFieldProps> = ({
  questions,
  selectedIds,
  onChange,
  onEditQuestion,
  referencingPackages = [],
}) => {
  const [warnExpanded, setWarnExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const [competencyFilter, setCompetencyFilter] = useState<CompetencySlug | typeof ALL>(ALL);
  const [typeFilter, setTypeFilter] = useState<QuestionType | typeof ALL>(ALL);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const cardsContainerRef = useRef<HTMLDivElement>(null);

  const questionById = useMemo(() => new Map(questions.map((q) => [q.id, q])), [questions]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filteredQuestions = useMemo(() => {
    const term = search.trim().toLowerCase();
    return questions.filter((q) => {
      if (competencyFilter !== ALL && q.competencyId !== competencyFilter) return false;
      if (typeFilter !== ALL && q.questionType !== typeFilter) return false;
      if (!term) return true;
      return (
        competencyName(q.competencyId).toLowerCase().includes(term) ||
        q.text.toLowerCase().includes(term)
      );
    });
  }, [questions, search, competencyFilter, typeFilter]);

  const toggle = (id: string) => {
    if (selectedSet.has(id)) onChange(selectedIds.filter((x) => x !== id));
    else onChange([...selectedIds, id]);
  };

  const reorder = (from: number, to: number) => {
    if (from === to) return;
    const next = [...selectedIds];
    const [moved] = next.splice(from, 1);
    // `to` is the target's index in the pre-removal array; removing `from`
    // shifts everything after it left by one, so insertion must account for
    // that shift when moving an item downward.
    next.splice(to > from ? to - 1 : to, 0, moved);
    onChange(next);
  };

  const remove = (id: string) => onChange(selectedIds.filter((x) => x !== id));

  const handleDragStart = (index: number) => (e: React.DragEvent) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Required for drag-and-drop to register in some browsers (notably Firefox).
    e.dataTransfer.setData('text/plain', String(index));
  };

  /** The original-array index to insert the dragged card before — `selectedIds.length`
   * means "insert after the last card" (append). Computed from the container rather
   * than per-card `dragover` targets so the gaps between cards (and the space below
   * the last card) are valid drop zones too — relying on landing precisely inside an
   * 80px-tall card is what caused drops to be rejected (and the card to snap back)
   * on near-miss. */
  const indexForClientY = (clientY: number): number => {
    const container = cardsContainerRef.current;
    if (!container) return selectedIds.length;
    const cards = Array.from(container.querySelectorAll<HTMLElement>('[data-card-index]'));
    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) {
        return Number(card.dataset.cardIndex);
      }
    }
    return selectedIds.length;
  };

  const handleContainerDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragIndex === null) return;
    const target = indexForClientY(e.clientY);
    if (target !== dragOverIndex) setDragOverIndex(target);
  };

  const handleContainerDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIndex !== null) reorder(dragIndex, indexForClientY(e.clientY));
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const columns: BuilderTableColumn<QuestionWithQuestionSets>[] = [
    {
      key: 'selected',
      title: '',
      render: (q) => (
        <span className={`${styles.checkbox} ${selectedSet.has(q.id) ? styles.checkboxOn : ''}`}>
          {selectedSet.has(q.id) && (
            <Check className={styles.checkboxIcon} style={{ width: '1em', height: '1em' }} />
          )}
        </span>
      ),
    },
    {
      key: 'text',
      title: 'Question',
      sortFn: (a, b) => a.text.localeCompare(b.text),
      render: (q) => (
        <div className={styles.questionCell}>{q.text}</div>
      ),
    },
    {
      key: 'competency',
      title: 'Competency',
      sortFn: (a, b) => competencyName(a.competencyId).localeCompare(competencyName(b.competencyId)),
      render: (q) => {
        const name = competencyName(q.competencyId);
        const tint = competencyChipColor(q.competencyId);
        return (
          <BuilderChip tint={tint} title={name}>
            {name}
          </BuilderChip>
        );
      },
    },
    {
      key: 'type',
      title: 'Type',
      sortFn: (a, b) => a.questionType.localeCompare(b.questionType),
      render: (q) => {
        const tint = typeChipColor(q.questionType);
        return (
          <BuilderChip tint={tint}>
            {QUESTION_TYPE_LABELS[q.questionType]}
          </BuilderChip>
        );
      },
    },
    {
      key: 'usedIn',
      title: 'Used in',
      sortFn: (a, b) => a.questionSets.length - b.questionSets.length,
      render: (q) =>
        q.questionSets.length > 0 ? (
          <Text type="secondary" className={styles.usedInCount}>
            {q.questionSets.length} set{q.questionSets.length > 1 ? 's' : ''}
          </Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      key: 'updated',
      title: 'Updated',
      sortFn: (a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
      render: (q) => <span className={styles.cellDate}>{formatDate(q.updatedAt)}</span>,
    },
  ];

  return (
    <div
      className={styles.field}
      ref={cardsContainerRef}
      onDragOver={dragIndex !== null ? handleContainerDragOver : undefined}
      onDrop={dragIndex !== null ? handleContainerDrop : undefined}
    >
      {referencingPackages.length > 0 && (
        <div className={styles.warnBox}>
          <button
            type="button"
            className={styles.warnHeader}
            onClick={() => setWarnExpanded((v) => !v)}
            aria-expanded={warnExpanded}
          >
            <TriangleAlert className={styles.warnIcon} style={{ width: '1em', height: '1em' }} />
            <span className={styles.warnHeaderText}>
              Used in {referencingPackages.length} assessment package
              {referencingPackages.length === 1 ? '' : 's'}
            </span>
            {warnExpanded ? (
              <ChevronUp className={styles.warnChevron} style={{ width: '1em', height: '1em' }} />
            ) : (
              <ChevronDown className={styles.warnChevron} style={{ width: '1em', height: '1em' }} />
            )}
          </button>
          {warnExpanded && (
            <div className={styles.warnBody}>
              Changes to this set will affect: {referencingPackages.map((pkg) => pkg.name).join(', ')}.
            </div>
          )}
        </div>
      )}

      <div className={styles.selectedHeader}>
        <Text strong className={styles.selectedHeaderLabel}>
          Questions in this set
        </Text>
        <Text className={styles.selectedCount}>· {selectedIds.length} selected</Text>
      </div>

      <div className={styles.selectedCards}>
        {selectedIds.length === 0 ? (
          <div className={styles.selectedEmpty}>Tick rows below to add questions to this set.</div>
        ) : (
          selectedIds.map((id, index) => {
            const q = questionById.get(id);
            if (!q) return null;
            const compTint = competencyChipColor(q.competencyId);
            const typeTint = typeChipColor(q.questionType);
            const isDragOverTarget =
              dragOverIndex === index ||
              // Dropping past the last card (append) has no card at `dragOverIndex`
              // to match against, so the last card stands in for that drop zone.
              (dragOverIndex === selectedIds.length && index === selectedIds.length - 1);
            const cardClasses = [
              styles.selectedCard,
              dragIndex === index ? styles.selectedCardDragging : '',
              isDragOverTarget && dragIndex !== index ? styles.selectedCardDragOver : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <div
                className={cardClasses}
                key={id}
                data-card-index={index}
                draggable
                onDragStart={handleDragStart(index)}
                onDragEnd={handleDragEnd}
              >
                <span className={styles.dragHandle} title="Drag to reorder">
                  <GripVertical style={{ width: '1em', height: '1em' }} />
                </span>
                <div className={styles.selectedCardBody}>
                  <div className={styles.selectedCardText}>{q.text}</div>
                  <div className={styles.selectedCardChips}>
                    <BuilderChip tint={compTint}>
                      {competencyName(q.competencyId)}
                    </BuilderChip>
                    <BuilderChip tint={typeTint}>
                      {QUESTION_TYPE_LABELS[q.questionType]}
                    </BuilderChip>
                  </div>
                </div>
                <div className={styles.selectedCardActions}>
                  {onEditQuestion && (
                    <BuilderIconButton
                      icon={<Pencil style={{ width: '1em', height: '1em' }} />}
                      title="Edit"
                      onClick={() => onEditQuestion(q)}
                    />
                  )}
                  <BuilderIconButton
                    icon={<X style={{ width: '1em', height: '1em' }} />}
                    title="Remove"
                    onClick={() => remove(id)}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      <hr className={styles.divider} />

      <div className={styles.toolbar}>
        <div className={styles.search}>
          <Search className={styles.searchIcon} style={{ width: '1em', height: '1em' }} />
          <Input
            placeholder="Search questions…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        <Select
          value={competencyFilter}
          onChange={setCompetencyFilter}
          options={COMPETENCY_FILTER_OPTIONS}
          showSearch
          optionFilterProp="label"
          className={styles.filterSelect}
          popupMatchSelectWidth={false}
        />
        <Select
          value={typeFilter}
          onChange={setTypeFilter}
          options={TYPE_FILTER_OPTIONS}
          className={styles.filterSelect}
        />
      </div>

      <BuilderTable<QuestionWithQuestionSets>
        columns={columns}
        gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
        data={filteredQuestions}
        rowKey={(q) => q.id}
        pageSize={PICKER_PER_PAGE}
        defaultSortKey="updated"
        onRowClick={(q) => toggle(q.id)}
        emptyTitle={questions.length ? 'No questions match' : 'The bank is empty'}
        emptySub="Add questions to the bank from the Question Bank page, then return here to select them."
      />
    </div>
  );
};

export default QuestionSetItemsField;
